import { useCallback, useEffect, useState } from 'react';

import { apiUserFetch, sharedPrefsEnabled, getSharedPrefs, saveSharedPrefs } from '../services/apiClient';
import type { Theme } from '../types/api';

const STORAGE_KEY = 'ignode-annotator-theme';

interface ThemeResponse {
    theme: Theme | null;
}

/**
 * useDarkMode — Agent Builder's `isDarkMode` boolean pattern.
 *
 * Returns the resolved theme + a setter, plus an isDarkMode boolean
 * convenience that consumers thread through their component tree.
 *
 * Persistence (active in BOTH standalone + IGNODE modes — backend persists
 * per-USER in the user_preferences table):
 *   - localStorage (always — synchronous on next reload, prevents flash
 *     during the async backend round-trip on mount)
 *   - backend POST /api/v1/users/<userId>/preferences/theme (always) —
 *     roams the preference across browsers/devices for the same user.
 *     Standalone reads userId from VITE_DEFAULT_USER_ID; IGNODE mode has
 *     the proxy rewrite :userId from the JWT. Best-effort: backend
 *     unreachable doesn't block the UI.
 *
 * The pre-paint script in `index.html` applies the localStorage value
 * BEFORE React mounts to prevent flash on dark-pref reloads.
 *
 * Preference scope is per-USER, NOT per-org — a user toggling light/dark
 * expects the choice to follow them across every org they belong to. So
 * this hook does NOT take an orgId.
 */
export function useDarkMode() {
    const [theme, setThemeState] = useState<Theme>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
        } catch { /* private mode */ }
        return 'system';
    });

    const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return false;
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    // Apply <html class="dark"> + track system pref changes.
    useEffect(() => {
        const root = document.documentElement;
        const dark = theme === 'dark' || (theme === 'system' && systemPrefersDark);
        root.classList.toggle('dark', dark);
    }, [theme, systemPrefersDark]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    // Pull server-side value on first mount. If the server has a stored
    // value AND it differs from what localStorage gave us, the server wins
    // (it reflects the most recent choice across the user's devices).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (sharedPrefsEnabled) {
                    // IGNODE mode — read from the shared cross-surface store.
                    const body = await getSharedPrefs();
                    if (cancelled) return;
                    if ((body.theme === 'light' || body.theme === 'dark') && body.theme !== theme) {
                        setThemeState(body.theme);
                        try { localStorage.setItem(STORAGE_KEY, body.theme); } catch { /* ignore */ }
                    }
                    return;
                }
                const body = await apiUserFetch<ThemeResponse>('/preferences/theme');
                if (cancelled || !body.theme) return;
                if (body.theme !== theme) {
                    setThemeState(body.theme);
                    try { localStorage.setItem(STORAGE_KEY, body.theme); } catch { /* ignore */ }
                }
            } catch { /* swallow — backend down ≠ break UI */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setTheme = useCallback((next: Theme) => {
        setThemeState(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
        // Fire-and-forget — UI already reflects the change locally.
        if (sharedPrefsEnabled) {
            // IGNODE mode — write to the shared cross-surface store. The
            // shared store only ever holds light/dark, so resolve `system`
            // to the current concrete value before writing.
            const resolved = next === 'system' ? (systemPrefersDark ? 'dark' : 'light') : next;
            saveSharedPrefs({ theme: resolved }).catch(() => { /* swallow */ });
        } else {
            apiUserFetch('/preferences/theme', undefined, {
                method: 'PUT',
                body: { theme: next },
            }).catch(() => { /* swallow */ });
        }
    }, [systemPrefersDark]);

    const isDarkMode = theme === 'dark' || (theme === 'system' && systemPrefersDark);

    return { theme, setTheme, isDarkMode };
}
